import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
// Will have to ignore this until flags is upgraded to use react-sweet-state instead
import { Subscribe } from 'unstated';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { VIEW_TASKS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';

import { LoadingAndPagination } from '../LoadingAndPagination.tsx';
import { HomeViewFilters } from '../homeViewsQueryHelpers.ts';
import { ExperienceTracking } from '../ExperienceTracking.tsx';
import TasksEmptyStateLight from '../images/tasks/TasksEmptyStateLight.svg';
import TasksEmptyStateDark from '../images/tasks/TasksEmptyStateDark.svg';

import { useTasksTableHead } from './useTasksTableHead.tsx';
import { useGetTasksQuery } from './useGetTasksQuery.ts';
import { TaskRowsProvider } from './TaskRowsContext.tsx';
import { TaskColumn, TaskView, SortTypes, SortOptions } from './tasksDefinitions.ts';
import { FiltersProvider } from './Filters/FiltersContext.tsx';
import { Filters } from './Filters/Filters.tsx';
import { useFiltersReducer } from './Filters/useFiltersReducer.ts';
import { useFormattedTasks } from './useFormattedTasks.tsx';
import { useTaskBatcher } from './useTaskBatcher.ts';
import { TaskViewPills } from './TaskViewPills.tsx';
import { FilterKeys } from './Filters/filtersDefinitions.ts';

const i18n = defineMessages({
	tasksHomeTitle: {
		id: 'home.views.tasks.title',
		defaultMessage: 'Tasks',
		description: "Header for the user's list of tasks",
	},
	taskNoResultsMessage: {
		id: 'home.views.tasks.noResultsMessage',
		defaultMessage: 'Nothing to show',
		description:
			'Header message to display when no tasks are found when a search filter is applied.',
	},
	taskNoResultsSubtext: {
		id: 'home.views.tasks.noResultsSubtext',
		defaultMessage: 'Adjust your filters and try again.',
		description:
			'Header message to display when no tasks are found when a search filter is applied.',
	},

	taskEmptyMessageHeaderAssigned: {
		id: 'home.views.tasks.taskEmptyMessageHeaderAssigned',
		defaultMessage: 'Sit back and relax',
		description: 'Header message to display when no assigned tasks are found.',
	},
	taskEmptyMessageBodyAssigned: {
		id: 'home.views.tasks.taskEmptyMessageBodyAssigned',
		defaultMessage: 'You have no tasks assigned to you',
		description: 'Message body to display when no tasks are found for assigned tasks.',
	},

	taskEmptyMessageHeaderCreated: {
		id: 'home.views.tasks.taskEmptyMessageHeaderCreated',
		defaultMessage: 'No tasks here',
		description: 'Header message to display when no created tasks are found.',
	},
	taskEmptyMessageBodyCreated: {
		id: 'home.views.tasks.taskEmptyMessageBodyCreated',
		defaultMessage: 'Enjoy your day!',
		description: 'Message body to display when no created tasks are found.',
	},
	taskSuccessFlag: {
		id: 'home.views.tasks.taskSuccessFlag',
		defaultMessage: 'Done!',
		description:
			'Title of the notification flag that informs the user the task updated successfully',
	},
	taskFlagBody: {
		id: 'home.views.tasks.taskFlagBody',
		defaultMessage: 'You completed {count, plural, one {# task} other {# tasks}}.',
		description: 'Tells user number of tasks updated',
	},
	taskSuccessIncompleteFlag: {
		id: 'home.views.tasks.taskSuccessIncompleteFlag',
		defaultMessage: '{count, plural, one {Task} other {Tasks}} Reopened',
		description: 'Flag title telling the user the number of tasks they marked incomplete',
	},
	taskIncompleteFlagBody: {
		id: 'home.views.tasks.taskIncompleteFlagBody',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"You've reopened {count , plural, one {# task} other {# tasks}} previously marked complete.",
		description: 'Flag body message telling the user they have reopened previously completed tasks',
	},
});

const tasksHomeContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	position: 'relative',
});

// Need to select and override styles like this instead of a className or css prop
// because otherwise we will override the styles already present.
const tableContainerStyles = css({
	backgroundColor: `${token('elevation.surface.raised')}`,
	padding: token('space.250'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	table: {
		borderCollapse: 'collapse',
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'table > tbody > tr': {
		borderBottom: `${token('border.width.selected')} solid ${token('color.border')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'table > tbody > tr > td': {
		paddingBottom: token('space.100'),
		paddingTop: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'table > thead > tr > th:first-of-type': {
		width: 610,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'table > tbody > tr > td:first-of-type': {},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'table > tbody > tr > th:nth-child(2)': {
		width: 270,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'table > tbody > tr > td:nth-child(2)': {},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'table > thead > tr > th:last-of-type': {
		width: 100,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'table > tbody > tr > td:last-of-type': {},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'table > thead > tr > th': {
		font: token('font.heading.xsmall'),
	},
});

const tasksHeaderStyles = css({
	font: token('font.heading.large'),
	margin: `${token('space.025')} 0 ${token('space.200')} 0`,
});

const tasksEmptyStateImage = {
	src: TasksEmptyStateLight,
	srcDark: TasksEmptyStateDark,
};

const tasksOptionsContainerStyles = css({
	marginLeft: token('space.negative.300'),
	paddingBottom: token('space.300'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const CHUNK_SIZE = 20;

export const TasksComponent = ({ flags }: { flags: FlagsStateContainer }) => {
	const intl = useIntl();
	const { add, data } = useTaskBatcher();
	const [filterState, filterDispatch] = useFiltersReducer();
	const [mainColumn, setMainColumn] = useState<TaskColumn>(TaskColumn.Page);

	const [sortType, setSortType] = useState<SortTypes>(SortTypes.Date);
	const [sortOption, setSortOption] = useState<SortOptions>(SortOptions.Ascending);

	const [assignedCursor, setAssignedCursor] = useState<number | null>(CHUNK_SIZE);
	const [createdCursor, setCreatedCursor] = useState<number | null>(CHUNK_SIZE);
	const [selectedTableView, setSelectedTableView] = useState<TaskView>(TaskView.Assigned);
	const [hasCompletedTasks, setHasCompletedTasks] = useState(false);
	const [numCompletedTasks, setNumCompletedTasks] = useState<number>(0);

	const { tasks, loadingState, error, getTasks, hasNextPage, refetch } = useGetTasksQuery({
		filters: filterState.filters,
		taskView: TaskView.Assigned,
		sortType,
		sortOption,
	});

	const {
		tasks: createdTasks,
		loadingState: createdLoadingState,
		error: createdError,
		getTasks: fetchMoreCreatedTasks,
		hasNextPage: hasNextCreatedPage,
		refetch: refetchCreatedTasks,
	} = useGetTasksQuery({
		filters: filterState.filters,
		taskView: TaskView.Created,
		sortType,
		sortOption,
	});

	// When the sortDirection changes, reset the chunk size because the query will reset the results.
	useEffect(() => {
		setAssignedCursor(CHUNK_SIZE);
		setCreatedCursor(CHUNK_SIZE);
	}, [sortOption, sortType]);

	useEffect(() => {
		if (hasCompletedTasks) {
			if (selectedTableView === TaskView.Created) {
				refetch();
			} else if (selectedTableView === TaskView.Assigned) {
				refetchCreatedTasks();
			}

			setHasCompletedTasks(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTableView]);

	useEffect(() => {
		// Use data from the mutation batch as a proxy for success
		if (data?.setBatchedTaskStatus?.success) {
			let title, description;

			if (filterState.filters[FilterKeys.COMPLETED] === 'done') {
				title = intl.formatMessage(i18n.taskSuccessIncompleteFlag, {
					count: numCompletedTasks,
				});
				description = intl.formatMessage(i18n.taskIncompleteFlagBody, {
					count: numCompletedTasks,
				});
			} else {
				title = intl.formatMessage(i18n.taskSuccessFlag);
				description = intl.formatMessage(i18n.taskFlagBody, {
					count: numCompletedTasks,
				});
			}

			if (numCompletedTasks > 0) {
				void flags.showSuccessFlag({
					title,
					description,
				});
			}
			setNumCompletedTasks(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	useEffect(() => {
		if (loadingState || createdLoadingState) return;
		// If our initial payload is less than what we display, there are no more "pages"
		if (!hasNextPage && !hasNextCreatedPage) {
			if (selectedTableView === TaskView.Assigned) {
				// 1. We're only dealing with one page of results
				if (assignedCursor && assignedCursor >= tasks.length) {
					// We're done paginating
					setAssignedCursor(null);
				}
			} else if (selectedTableView === TaskView.Created) {
				if (createdCursor && createdCursor >= tasks.length) {
					setCreatedCursor(null);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTableView, tasks, loadingState, createdLoadingState, sortOption]);

	const updateTasks = useCallback(
		(item: TaskAction) => {
			if (item.state === 'DONE') {
				setHasCompletedTasks(true);
				setNumCompletedTasks((prev) => prev + 1);
				add(item);
			}
		},
		[add],
	);

	const { rows, ...options } = useFormattedTasks({
		cursor: selectedTableView === TaskView.Assigned ? assignedCursor : createdCursor,
		tasks: selectedTableView === TaskView.Assigned ? tasks : createdTasks,
		mainColumn,
		onTaskCheckboxClick: updateTasks,
	});

	const head = useTasksTableHead({
		mainColumn,
		setMainColumn,
		setSortOption,
		setSortType,
	});

	const getNextTasks = () => {
		// Based on the current view, load the next "page" of tasks
		if (selectedTableView === TaskView.Assigned) {
			if (!assignedCursor || loadingState) return;

			// Check if we need to actually fetch more tasks
			if (hasNextPage && Number(assignedCursor) >= tasks.length && !loadingState) {
				getTasks();
				setAssignedCursor((prev) => Number(prev) + CHUNK_SIZE);
				return;
			}

			// If we don't, just grab the next "page"
			const nextPage = tasks.slice(assignedCursor, assignedCursor + CHUNK_SIZE);

			if (nextPage.length < CHUNK_SIZE && !hasNextPage) {
				// There are no more pages
				setAssignedCursor(null);
			} else {
				// Move the cursor forward
				setAssignedCursor((prev) => Number(prev) + CHUNK_SIZE);
			}
		} else if (selectedTableView === TaskView.Created) {
			if (!createdCursor || createdLoadingState) return;

			if (
				hasNextCreatedPage &&
				Number(createdCursor) >= createdTasks.length &&
				!createdLoadingState
			) {
				fetchMoreCreatedTasks();
				setCreatedCursor((prev) => Number(prev) + CHUNK_SIZE);
				return;
			}

			const nextPage = tasks.slice(createdCursor, createdCursor + CHUNK_SIZE);

			if (nextPage.length < CHUNK_SIZE && !hasNextCreatedPage) {
				// There are no more pages
				setCreatedCursor(null);
			} else {
				// Move the cursor forward
				setCreatedCursor((prev) => Number(prev) + CHUNK_SIZE);
			}
		}
	};

	return (
		<div css={tasksHomeContainerStyles}>
			<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.tasksHomeTitle)} />
			<ExperienceTracking
				experienceName={VIEW_TASKS_EXPERIENCE}
				loading={loadingState}
				error={error}
				attributes={{ filters: JSON.stringify(filterState.filters) }}
			/>
			{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
			<h1 css={tasksHeaderStyles}>
				<FormattedMessage {...i18n.tasksHomeTitle} />
			</h1>
			<div css={tasksOptionsContainerStyles}>
				<TaskViewPills
					tasksLength={tasks.length}
					createdTasksLength={createdTasks.length}
					selectedTableView={selectedTableView}
					setSelectedTableView={setSelectedTableView}
				/>
				<Filters
					selectedTableView={selectedTableView}
					options={options}
					filterDispatch={filterDispatch}
					filterState={filterState}
				/>
			</div>
			{!error && rows.length > 0 ? (
				<div css={tableContainerStyles}>
					<DynamicTableStateless head={head} rows={rows} />
				</div>
			) : null}
			<LoadingAndPagination
				pageCardDataLength={rows.length}
				hasNextPage={selectedTableView === TaskView.Assigned ? !!assignedCursor : !!createdCursor}
				error={error || createdError}
				loading={loadingState || createdLoadingState}
				loadMore={getNextTasks}
				noResultsSubtext={i18n.taskNoResultsSubtext}
				noResultsMessage={i18n.taskNoResultsMessage}
				emptyMessageHeader={
					selectedTableView === TaskView.Assigned
						? i18n.taskEmptyMessageHeaderAssigned
						: i18n.taskEmptyMessageHeaderCreated
				}
				emptyMessageBody={
					selectedTableView === TaskView.Assigned
						? i18n.taskEmptyMessageBodyAssigned
						: i18n.taskEmptyMessageHeaderCreated
				}
				emptyMessageImage={tasksEmptyStateImage}
				filterText={filterState.count > 0 ? 'taskFilter' : ''}
				source="tasks"
				filter={HomeViewFilters.TASKS}
			/>
		</div>
	);
};

export const Tasks = () => {
	return (
		<UFOSegment name="all-tasks-page">
			<div data-testid="home_tasks">
				<FiltersProvider>
					<TaskRowsProvider>
						<Subscribe to={[FlagsStateContainer]}>
							{(flags: FlagsStateContainer) => (
								<Fragment>
									<TasksComponent flags={flags} />
								</Fragment>
							)}
						</Subscribe>
					</TaskRowsProvider>
				</FiltersProvider>
			</div>
		</UFOSegment>
	);
};
